import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, MessageCircle, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { serviceIcons } from "../components/ServiceCard";
import { useLang } from "../context/LanguageContext";
import { api, waLink, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const AREAS = ["Kuala Lumpur", "Petaling Jaya", "Shah Alam", "Klang", "Subang Jaya", "Ampang", "Cheras", "Puchong", "Kajang", "Putrajaya", "Gombak", "Rawang"];
const TIMES = ["09:00", "10:00", "11:00", "12:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00"];

export default function Booking() {
  const { lang, t } = useLang();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [form, setForm] = useState({
    service_id: params.get("service") || "",
    date: "", time: "",
    doctor_id: params.get("doctor") || "",
    patient_name: "", phone: "", email: "", address: "", area: "", notes: "",
  });

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/doctors").then((r) => setDoctors(r.data.filter((d) => d.available))).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const selService = services.find((s) => s.id === form.service_id);
  const selDoctor = doctors.find((d) => d.id === form.doctor_id);

  const canNext = () => {
    if (step === 1) return !!form.service_id;
    if (step === 2) return form.date && form.time;
    if (step === 3) return form.patient_name && form.phone && form.address && form.area;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form, doctor_id: form.doctor_id || null };
      const { data } = await api.post("/bookings", payload);
      setConfirmation(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    const waMsg = lang === "ms"
      ? `Hai, saya baru membuat tempahan (Rujukan: ${confirmation.booking_ref}) untuk ${confirmation.service_name} pada ${confirmation.date} ${confirmation.time}. Mohon pengesahan.`
      : `Hi, I just made a booking (Ref: ${confirmation.booking_ref}) for ${confirmation.service_name} on ${confirmation.date} at ${confirmation.time}. Please confirm.`;
    return (
      <div data-testid="booking-confirmation" className="max-w-2xl mx-auto px-4 py-24 text-center">
        <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-6" />
        <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 mb-4">{t("book.success_title")}</h1>
        <p className="text-slate-500 mb-2">{t("book.success_msg")}</p>
        <p data-testid="booking-ref" className="font-heading text-2xl font-semibold text-primary mb-6">{confirmation.booking_ref}</p>
        <p className="text-sm text-slate-500 leading-relaxed mb-8 max-w-md mx-auto">{t("book.success_note")}</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a href={waLink(waMsg)} target="_blank" rel="noopener noreferrer" data-testid="confirmation-whatsapp-btn"
            className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full bg-[#25D366] text-white font-semibold hover:scale-105 transition-transform">
            <MessageCircle className="w-5 h-5" /> {t("book.wa_confirm")}
          </a>
          <Button variant="outline" data-testid="new-booking-btn" className="rounded-full h-12 px-8" onClick={() => { setConfirmation(null); setStep(1); setForm({ service_id: "", date: "", time: "", doctor_id: "", patient_name: "", phone: "", email: "", address: "", area: "", notes: "" }); }}>
            {t("book.new")}
          </Button>
        </div>
        <Link to={`/track?ref=${confirmation.booking_ref}`} data-testid="confirmation-track-link"
          className="inline-block mt-6 text-sm font-semibold text-primary hover:underline">
          {t("book.track_link")} →
        </Link>
      </div>
    );
  }

  const steps = ["book.step1", "book.step2", "book.step3", "book.step4"];

  return (
    <div data-testid="booking-page" className="max-w-2xl mx-auto px-4 sm:px-6 py-16">
      <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 mb-4">{t("book.title")}</h1>
      <p className="text-base text-slate-500 mb-10">{t("book.subtitle")}</p>

      {/* Progress */}
      <div className="flex items-center mb-12">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center">
              <span data-testid={`booking-step-${i + 1}`}
                className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold ${step > i + 1 ? "bg-emerald-500 text-white" : step === i + 1 ? "bg-primary text-white" : "bg-slate-100 text-slate-400"}`}>
                {step > i + 1 ? <Check className="w-4 h-4" /> : i + 1}
              </span>
              <span className={`text-xs mt-2 hidden sm:block ${step === i + 1 ? "text-primary font-semibold" : "text-slate-400"}`}>{t(s)}</span>
            </div>
            {i < steps.length - 1 && <div className={`flex-1 h-0.5 mx-2 mb-0 sm:mb-6 ${step > i + 1 ? "bg-emerald-500" : "bg-slate-200"}`} />}
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-8">
        {step === 1 && (
          <div className="space-y-3">
            <Label className="text-base font-semibold">{t("book.select_service")}</Label>
            <p data-testid="booking-travel-note" className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">{t("services.travel_note")}</p>
            {services.map((s) => {
              const Icon = serviceIcons[s.icon];
              return (
                <button key={s.id} onClick={() => set("service_id", s.id)} data-testid={`booking-service-${s.id}`}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border text-left transition-colors ${form.service_id === s.id ? "border-primary bg-primary/5" : "border-slate-200 hover:border-slate-300"}`}>
                  {Icon && <Icon className={`w-5 h-5 ${form.service_id === s.id ? "text-primary" : "text-slate-400"}`} />}
                  <span className="flex-1">
                    <span className="block font-medium text-slate-900">{lang === "ms" ? s.name_ms : s.name_en}</span>
                    <span className="text-sm text-slate-500">{t("services.from")} {s.price_from} • {s.duration}</span>
                  </span>
                  {form.service_id === s.id && <Check className="w-5 h-5 text-primary" />}
                </button>
              );
            })}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <div>
              <Label htmlFor="date">{t("book.date")}</Label>
              <Input id="date" type="date" data-testid="booking-date-input" min={new Date().toISOString().split("T")[0]}
                value={form.date} onChange={(e) => set("date", e.target.value)} className="mt-2" />
            </div>
            <div>
              <Label>{t("book.time")}</Label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-2">
                {TIMES.map((tm) => (
                  <button key={tm} onClick={() => set("time", tm)} data-testid={`booking-time-${tm}`}
                    className={`py-2 rounded-lg text-sm font-medium border transition-colors ${form.time === tm ? "bg-primary text-white border-primary" : "border-slate-200 text-slate-600 hover:border-primary"}`}>
                    {tm}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>{t("book.doctor")}</Label>
              <Select value={form.doctor_id || "any"} onValueChange={(v) => set("doctor_id", v === "any" ? "" : v)}>
                <SelectTrigger data-testid="booking-doctor-select" className="mt-2"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">{t("book.any_doctor")}</SelectItem>
                  {doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} — {d.specialization}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5">
            <div>
              <Label htmlFor="name">{t("book.name")}</Label>
              <Input id="name" data-testid="booking-name-input" value={form.patient_name} onChange={(e) => set("patient_name", e.target.value)} className="mt-2" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="phone">{t("book.phone")}</Label>
                <Input id="phone" data-testid="booking-phone-input" placeholder="012-3456789" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-2" />
              </div>
              <div>
                <Label htmlFor="email">{t("book.email")}</Label>
                <Input id="email" type="email" data-testid="booking-email-input" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-2" />
              </div>
            </div>
            <div>
              <Label htmlFor="address">{t("book.address")}</Label>
              <Textarea id="address" data-testid="booking-address-input" rows={2} value={form.address} onChange={(e) => set("address", e.target.value)} className="mt-2" />
            </div>
            <div>
              <Label>{t("book.area")}</Label>
              <Select value={form.area} onValueChange={(v) => set("area", v)}>
                <SelectTrigger data-testid="booking-area-select" className="mt-2"><SelectValue placeholder={t("book.area")} /></SelectTrigger>
                <SelectContent>
                  {AREAS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="notes">{t("book.notes")}</Label>
              <Textarea id="notes" data-testid="booking-notes-input" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-2" />
            </div>
          </div>
        )}

        {step === 4 && (
          <div>
            <h3 className="font-heading text-lg font-semibold text-slate-900 mb-6">{t("book.review")}</h3>
            <dl className="space-y-4 text-sm" data-testid="booking-review">
              <div className="flex justify-between border-b border-slate-100 pb-3"><dt className="text-slate-500">{t("book.service_label")}</dt><dd className="font-medium text-slate-900">{selService && (lang === "ms" ? selService.name_ms : selService.name_en)}</dd></div>
              <div className="flex justify-between border-b border-slate-100 pb-3"><dt className="text-slate-500">{t("book.datetime_label")}</dt><dd className="font-medium text-slate-900">{form.date} • {form.time}</dd></div>
              <div className="flex justify-between border-b border-slate-100 pb-3"><dt className="text-slate-500">{t("book.doctor_label")}</dt><dd className="font-medium text-slate-900">{selDoctor ? selDoctor.name : t("book.any_doctor")}</dd></div>
              <div className="flex justify-between border-b border-slate-100 pb-3"><dt className="text-slate-500">{t("book.patient_label")}</dt><dd className="font-medium text-slate-900">{form.patient_name} • {form.phone}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">{t("book.location_label")}</dt><dd className="font-medium text-slate-900 text-right max-w-[60%]">{form.address}, {form.area}</dd></div>
            </dl>
          </div>
        )}

        <div className="flex justify-between mt-10">
          {step > 1 ? (
            <Button variant="outline" data-testid="booking-back-btn" className="rounded-full px-8" onClick={() => setStep(step - 1)}>{t("book.back")}</Button>
          ) : <span />}
          {step < 4 ? (
            <Button data-testid="booking-next-btn" className="rounded-full px-8" disabled={!canNext()} onClick={() => setStep(step + 1)}>{t("book.next")}</Button>
          ) : (
            <Button data-testid="booking-submit-btn" className="rounded-full px-8 bg-emerald-600 hover:bg-emerald-700" disabled={submitting} onClick={submit}>
              {submitting ? "..." : t("book.submit")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
